import React, { useEffect, useMemo } from 'react';
import { CommonPageWrapper } from 'admin/components/wrappers';
import styled from 'styled-components';
import { PageMainLabel } from 'app/components/SHLabel';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import Logo from 'app/assets/icons/yeda-logo.png';
import { PageMainSubLabel } from 'app/components/labels';
import useTeamReportsApi from 'admin/hooks/useTeamReportsApi';
import InnerViewTable from './InnerViewTable';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';

type Props = {};

const InnerViewTeams = (props: Props) => {
    const navigate = useNavigate();
    const { getTeamRankingInnerViewDetails, teamInnerViewData, loading } = useTeamReportsApi();
    const { id } = useParams();

    const transformRiderData = (riders) => {
        return riders?.map(rider => {
            const showsObject: any = {};
            rider?.showResults?.forEach((show: any) => {
                showsObject[show?.showId] = show?.points ?? '-';
            });
            return {
                riderId: rider?.riderId,
                firstName: rider?.firstName,
                lastName: rider?.lastName,
                backNumber: rider?.backNumber,
                totalRides: rider?.totalRides,
                pointsTotal: rider?.pointsTotal,
                ...showsObject,
            };
        });
    };

    const getUniqueShows = (riders) => {
        const showsMap = new Map();
        riders?.forEach(rider => {
            rider?.showResults?.forEach((show: any) => {
                if (!showsMap.has(show?.showId)) {
                    showsMap.set(show?.showId, {
                        id: show?.showId,
                        name: show?.showName,
                        date: show?.showDate,
                    });
                }
            });
        });
        return Array.from(showsMap.values());
    };

    // Combine all divisions into a single dataset
    const combinedTableData = useMemo(() => {
        const divisions = teamInnerViewData?.divisions || [];

        if (divisions.length === 0) return { gridData: [], columns: [] };

        // Collect all unique shows across all divisions
        const allShows = new Map();
        divisions.forEach(division => {
            const shows = getUniqueShows(division.riders);
            shows.forEach(show => {
                if (!allShows.has(show.id)) {
                    allShows.set(show.id, show);
                }
            });
        });

        // Build combined grid data with division separators
        const combinedGridData: any[] = [];

        divisions.forEach((division, divIndex) => {
            // Add division row
            const divisionRow: any = {
                _isDivisionRow: true,
                _divisionName: division.divisionName,
            };
            combinedGridData.push(divisionRow);

            // Transform and add riders for this division
            const transformedRiders = transformRiderData(division.riders);

            transformedRiders.forEach(rider => {
                const result: any = {
                    totalRides: rider.totalRides,
                    firstName: rider.firstName,
                    lastName: rider.lastName,
                    backNumber: rider.backNumber,
                    pointsTotal: rider.pointsTotal,
                };

                // Add all shows data, using empty string for shows not in this division
                Array.from(allShows.values()).forEach((show: any) => {
                    result[show.name] = {
                        id: show.id,
                        value: rider[show.id] || '-',
                        date: show.date,
                    };
                });

                combinedGridData.push(result);
            });
        });

        // Generate columns based on all unique shows
        const allShowsArray = Array.from(allShows.values());
        const columns = [
            { field: 'totalRides', title: 'totalRides', width: 100, locked: true },
            { field: 'firstName', title: 'firstName', width: 150, locked: true },
            { field: 'lastName', title: 'lastName', width: 150, locked: true },
            { field: 'backNumber', title: 'backNumber', width: 70, locked: true },
            { field: 'pointsTotal', title: 'pointsTotal', width: 100, locked: true },
            ...allShowsArray.map(show => ({
                field: show.name,
                title: show.name,
                width: 180,
                locked: false,
                showInfo: show,
            })),
        ];

        return { gridData: combinedGridData, columns };
    }, [teamInnerViewData]);

    useEffect(() => {
        if (id) getTeamRankingInnerViewDetails(id);
    }, [id]);

    return (
        <>
            <StyledCommonPageWrapper style={{ marginBottom: '50px' }}>
                <HeaderWrapper>
                    <img src={Logo} alt="showeyda-logo" onClick={() => navigate(`/`)} />
                </HeaderWrapper>
                <HeaderWrapper $spacebetween>
                    <SubHeadingWrapper>
                        {loading ?
                            <Skeleton width={150} height={20} /> :
                            <PageMainLabel>{teamInnerViewData?.teamName}</PageMainLabel>}
                        <FlexWrapper>
                            {loading ?
                                <>
                                    <Skeleton width={150} height={20} />
                                    <Skeleton width={150} height={20} />
                                </>
                                :
                                <>
                                    <PageMainSubLabel>{teamInnerViewData?.season}</PageMainSubLabel>
                                    <PageMainSubLabel className="red-style">
                                        Visit your Division's Ranking Link to see if you qualify for Finals.
                                    </PageMainSubLabel>
                                </>
                            }
                        </FlexWrapper>
                        {loading ?
                            <Skeleton width={150} height={20} /> :
                            <PageMainSubLabel>Individual Team Rider Ranking</PageMainSubLabel>}
                    </SubHeadingWrapper>
                </HeaderWrapper>

                {loading ? (
                    <TableSkeletonLoader rowContent={3} width={100} headingWidth={40} />
                ) : combinedTableData.gridData.length > 0 ? (
                    <InnerViewTable
                        data={combinedTableData.gridData}
                        columns={combinedTableData.columns}
                        isMultiDivision={true}
                    />
                ) : null}
            </StyledCommonPageWrapper>
        </>
    );
};

export default InnerViewTeams;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 100px;
`;

const SubHeadingWrapper = styled.div`
    display: flex;
    justify-content: start;
    gap: 10px;
    align-items: start;
    flex-direction: column;
    margin-bottom: 20px;
`;

const HeaderWrapper = styled.div<{ $spacebetween?: boolean }>`
    display: flex;
    justify-content: ${({ $spacebetween }) =>
        $spacebetween ? 'space-between' : 'flex-start'};
    gap: 100px;
    align-items: ${({ $spacebetween }) =>
        $spacebetween ? 'start' : 'centre'};
    padding: 10px 0 30px 0;

    .fill-button {
        margin-right: 0px;
    }

    .red-style {
        color: #cc0000;
    }
`;

const StyledCommonPageWrapper = styled(CommonPageWrapper)`
    padding-top: 26px;
    margin-bottom: unset !important;
`;